import { eventsStore, poiStore, gmapStore, kioskStore } from '../managers/globalState';

class RouteManager {
	constructor(history) {
		this._history = history;
		this._query = {};

		// Bind handlers.
		this._onEventsShowOnGlobe = this._onEventsShowOnGlobe.bind(this);

		// Bind handlers.
		this._onPoIShowOnGlobe = this._onPoIShowOnGlobe.bind(this);

		// Bind handlers.
		this._onGmapShowOnGlobe = this._onGmapShowOnGlobe.bind(this);

		// Subscribe to global state changes.
		this._subscribeToStates();
	}

	/**
	 * Parse a string and return a query object.
	 * @param {string} queryString
	 * @returns {object}
	 */
	static parseQuery(queryString) {
		if (!queryString) {
			return {};
		}

		const parts = queryString.split('?');
		if (parts.length > 1) {
			queryString = parts[1];
		}

		const query = queryString.split('&').reduce((obj, str) => {
			const parts = str.split('=');
			if (parts.length > 1) {
				let val = parts[1].trim();
				switch (val.toLowerCase()) {
					case 'true':
						val = true;
						break;
					case 'false':
						val = false;
						break;
					case 'undefined':
						val = undefined;
						break;
					case 'null':
						val = null;
						break;
					default: {
						const num = Number(val);
						if (!Number.isNaN(num)) {
							val = num;
						}
						break;
					}
				}
				obj[parts[0].trim()] = val;
			}
			return obj;
		}, {});

		return query;
	}

	/**
	 * Build a query string from a query object.
	 * @param {object} query
	 * @returns {string}
	 */
	buildQuery(query) {
		let queryString = '';

		for (const [key, value] of Object.entries(query)) {
			queryString += '&' + key + '=' + value;
		}

		// Replace the first `&` with `?`
		queryString = queryString.replace('&', '?');

		return queryString;
	}

	/**
	 * Add query to the stored query.
	 * @param {object} query
	 * @returns {object} Updated query
	 */
	addQuery(query) {
		this._query = { ...this._query, ...query };
		return this._query;
	}

	/**
	 * Remove query from the stored query.
	 * @param {Array} [keys=[]] - Query keys to remove
	 * @returns {object} Updated query
	 */
	removeQuery(keys = []) {
		keys.forEach(key => delete this._query[key]);
		return this._query;
	}

	/**
	 * Update, add to, or remove from the stored query.
	 * @param {object} query
	 * @returns {object} Updated query
	 */
	updateQuery(query) {
		if (query.__remove) {
			this.removeQuery(query.__remove);
			delete query.__remove;
		}
		return this.addQuery(query);
	}

	/**
	 * Update, add to, or remove from path query.
	 * @param {object} query
	 * @param {string} [pathname=''] - Base path
	 * @returns {string} Updated path
	 */
	updateQueryPath(query, pathname = '') {
		if (pathname === '') {
			pathname = this._history.location.pathname;
		}
		return pathname.replace(/(\/)(?=\/*\1)/, '') + this.buildQuery(this.updateQuery(query));
	}

	/**
	 * Attach callbacks to global states.
	 */
	_subscribeToStates() {
		eventsStore.subscribeTo('showOnGlobe', this._onEventsShowOnGlobe);
		poiStore.subscribeTo('showOnGlobePoI', this._onPoIShowOnGlobe);
	}

	/**
	 * Attach callbacks to global states.
	 */
	 _subscribeToStates() {
		eventsStore.subscribeTo('showOnGlobe', this._onEventsShowOnGlobe);
		gmapStore.subscribeTo('showOnGlobeGmap', this._onGmapShowOnGlobe);
	}

	_onEventsShowOnGlobe(showOnGlobe) {
		// Check whether we're already on an event, if so, reset it
		if (showOnGlobe && this._history?.location?.pathname) {
			this.resetNavigation();
		}
	}

	_onPoIShowOnGlobe(showOnGlobePoI) {
		// Check whether we're already on an poi, if so, reset it
		if (showOnGlobePoI && this._history?.location?.pathname) {
			this.resetNavigation();
		}
	}

	_onGmapShowOnGlobe(showOnGlobeGmap) {
		// Check whether we're already on an gmap, if so, reset it
		if (showOnGlobeGmap && this._history?.location?.pathname) {
			this.resetNavigation();
		}
	}

	/**
	 * Navigate to an event given the eventId
	 * @param {string} eventId
	 */
	goToEvent(eventId) {
		this.navigate({
			animating: 'f',
			start: '',
			end: '',
      vitalsign: 'visible_earth',
			__remove: ['videoId', 'showOnGlobe']
		}, `event/${eventId}`);
	}

	/**
	 * Navigate to an poi given the poiId
	 * @param {string} poiId
	 */
	 goToPoI(poiId) {
		poiStore.setGlobalState({ showOnGlobePoI: false });
		this.navigate({
			animating: 'f',
			start: '',
			end: '',
      vitalsign: 'visible_earth',
		}, `poi/${poiId}`);
	}

	/**
	 * Navigate to an gmap given the gmapId
	 * @param {string} gmapId
	 */
	 goToGmap(gmapId) {
		gmapStore.setGlobalState({ showOnGlobeGmap: false });
		this.navigate({
			animating: 'f',
			start: '',
			end: '',
      vitalsign: 'visible_earth',
		}, `gmap/${gmapId}`);
	}

	/**
	 * Resets back to no base.
	 */
	resetNavigation() {
		this.navigate({
			animating: 'f',
			start: '',
			end: ''
		}, '/');
	}

	/**
	 * Navigate to a path if it is not the current path.
	 * @param {string|object} route - Full path to navigate or query object
	 * @param {string} [base=''] - Base path if navigate with query
	 * @param {boolean} [force=false] - Force navigation
	 */
	navigate(route, base = '', force = false) {
		const { kioskUrl, playAutoplay } = kioskStore.stateSnapshot;

		if (typeof route === 'string') {
			const current = this._history.location.pathname + this._history.location.search;
			if (!route.startsWith('/')) {
				route = '/' + route;
			}

			// Retain kiosk and autoplay url parameters on navigation
			if (kioskUrl === true && !route.includes('kiosk=true')) {
				route += '&kiosk=true';
				route += `&autoplay=${playAutoplay}`;
			}

			if (current !== route || force) {
				const parts = route.split('?');

				if (parts.length > 1) {
					this._query = RouteManager.parseQuery(parts[1]);
				}

				this._history.push(route);
				return true;
			}
			return false;
		} else {
			return this.navigate(this.updateQueryPath(route, base), base, force);
		}
	}

	get history() {
		return this._history;
	}

	set history(input) {
		this._history = input;
	}

	get query() {
		return this._query;
	}

	set query(input) {
		this._query = input;
	}
}

export default RouteManager;
